/**
 * Where the captain looks (pure): a point on the screen becomes a head turn plus an eye turn. The bust is a small
 * window near the bubble's edge, and the player's face is imagined straight out of the screen, `VIEW_DEPTH_PX` away:
 * a target that far to the side is 45° off. The head takes most of the turn up to its limits, the eyes the rest.
 */

const DEG = Math.PI / 180;
export const LookLimits = {
  /** The contract's comfortable range for the head bone… */
  HeadYaw: 25 * DEG, HeadPitch: 15 * DEG,
  /** …and for the eyes. */
  Eye: 20 * DEG,
  /** Share of the turn the head does before the eyes help. */
  HeadShare: 0.65,
} as const;
/** The imagined distance from the screen to the player's eyes, in CSS px (a phone at arm's length). */
export const VIEW_DEPTH_PX = 420;

export interface LookAngles { readonly headYaw: number; readonly headPitch: number; readonly eyeYaw: number; readonly eyePitch: number }
export const LOOK_AHEAD: LookAngles = { headYaw: 0, headPitch: 0, eyeYaw: 0, eyePitch: 0 };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Angles to look at a screen point `dx, dy` CSS px from the captain's eyes (screen y grows down). Positive yaw turns
 * him to his left, which is screen right as he faces the player; positive pitch looks up. `mirrored` is a bust drawn
 * flipped (pointing left).
 */
export function lookAngles(dx: number, dy: number, mirrored = false): LookAngles {
  const yaw = Math.atan2(mirrored ? -dx : dx, VIEW_DEPTH_PX);
  const pitch = Math.atan2(-dy, VIEW_DEPTH_PX);
  const headYaw = clamp(yaw * LookLimits.HeadShare, -LookLimits.HeadYaw, LookLimits.HeadYaw);
  const headPitch = clamp(pitch * LookLimits.HeadShare, -LookLimits.HeadPitch, LookLimits.HeadPitch);
  return {
    headYaw, headPitch,
    eyeYaw: clamp(yaw - headYaw, -LookLimits.Eye, LookLimits.Eye),
    eyePitch: clamp(pitch - headPitch, -LookLimits.Eye, LookLimits.Eye),
  };
}

/** Frame-rate independent ease toward a target: `rate` is how many e-foldings a second. */
export function damp(current: number, target: number, rate: number, dtSec: number): number {
  return target + (current - target) * Math.exp(-rate * dtSec);
}

/** Small eye darts while he looks at the player, so the stare is alive (every 1.2–3.5 s, within ±4° by ±2.5°). */
export const Wander = { MinMs: 1200, MaxMs: 3500, Yaw: 4 * DEG, Pitch: 2.5 * DEG } as const;

export class LookWander {
  private next: number;
  private cur = { yaw: 0, pitch: 0 };
  constructor(private readonly rng: () => number, now: number) { this.next = now + this.gap(); }
  /** The dart at `now` (non-decreasing); true in `moved` on the frame it jumps (a blink goes well with it). */
  at(now: number): { yaw: number; pitch: number; moved: boolean } {
    if (now < this.next) return { ...this.cur, moved: false };
    this.next = now + this.gap();
    this.cur = { yaw: (this.rng() * 2 - 1) * Wander.Yaw, pitch: (this.rng() * 2 - 1) * Wander.Pitch };
    return { ...this.cur, moved: true };
  }
  private gap(): number { return Wander.MinMs + (Wander.MaxMs - Wander.MinMs) * this.rng(); }
}
